import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { apiClient } from '../lib/apiClient'
import { legalService, LegalDocument } from '../services/legalService'
import { validatePassword, getPasswordStrengthColor } from '../utils/passwordValidator'
import { rateLimiter, RATE_LIMIT_CONFIGS } from '../utils/rateLimiter'
import { validateEmail, sanitizeInput } from '../utils/validators'
import styles from './LoginPage.module.css'
import { Shield } from '../components/icons/Icons'

export default function SignupPage() {
  const { signUp } = useAuth()
  const navigate = useNavigate()

  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [orgName, setOrgName] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false)
  const [acceptedAUP, setAcceptedAUP] = useState(false)
  const [requiredDocs, setRequiredDocs] = useState<LegalDocument[]>([])
  const [passwordStrength, setPasswordStrength] = useState<ReturnType<typeof validatePassword> | null>(null)
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false)

  useEffect(() => {
    const loadRequiredDocs = async () => {
      try {
        const docs = await legalService.getRequiredDocuments()
        setRequiredDocs(docs)
      } catch (err) {
        console.error('Error loading legal documents:', err)
      }
    }
    loadRequiredDocs()
  }, [])

  const handlePasswordChange = (value: string) => {
    setPassword(value)
    if (value) {
      const strength = validatePassword(value)
      setPasswordStrength(strength)
    } else {
      setPasswordStrength(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const trimmedEmail = email.trim()
    if (!validateEmail(trimmedEmail)) {
      setError('Please enter a valid email address')
      return
    }

    const rateCheck = rateLimiter.check(trimmedEmail, 'signup', RATE_LIMIT_CONFIGS.SIGNUP.maxAttempts, RATE_LIMIT_CONFIGS.SIGNUP.windowMs)
    if (!rateCheck.allowed) {
      const resetMinutes = Math.ceil((rateCheck.resetTime - Date.now()) / 60000)
      setError(`Too many signup attempts. Please try again in ${resetMinutes} minute${resetMinutes !== 1 ? 's' : ''}`)
      return
    }

    if (!acceptedTerms || !acceptedPrivacy || !acceptedAUP) {
      setError('You must accept the Terms of Service, Privacy Policy, and Acceptable Use Policy to create an account.')
      return
    }

    const passwordValidation = validatePassword(password)
    if (!passwordValidation.isValid) {
      setError(passwordValidation.feedback.join('. '))
      return
    }

    setLoading(true)
    try {
      const sanitizedDisplayName = sanitizeInput(displayName, 100)
      const sanitizedOrgName = sanitizeInput(orgName, 100)

      if (!sanitizedDisplayName || !sanitizedOrgName) {
        setError('Name and organization name cannot be empty')
        setLoading(false)
        return
      }

      const { error: signUpErr } = await signUp(trimmedEmail, password, sanitizedDisplayName, sanitizedOrgName)
      if (signUpErr) { setError(signUpErr.message); setLoading(false); return }

      const currentUser = await apiClient.get<{ user: { id: string }; organization: { id: string } }>('/auth/me').catch(() => null)
      if (currentUser && requiredDocs.length > 0) {
        const userId = (currentUser as any).user?.id
        const orgId = (currentUser as any).organization?.id
        if (userId && orgId) {
          const acceptedDocs = requiredDocs.map(doc => ({
            type: doc.document_type,
            version: doc.version
          }))
          try {
            await legalService.recordMultipleAcceptances(userId, orgId, acceptedDocs, 'signup')
          } catch (acceptanceErr) {
            console.error('Failed to record legal acceptances after signup:', acceptanceErr)
          }
        }
      }

      navigate('/verify-email')
    } catch (err: any) {
      setError(err.message || 'An error occurred')
      setLoading(false)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <div className={styles.logoIcon}><Shield size={28} /></div>
          <div>
            <h1 className={styles.appName}>AllocatIQ</h1>
            <p className={styles.tagline}>Resource Forecasting & Utilization</p>
          </div>
        </div>

        <h2 className={styles.heading}>Create your account</h2>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.error}>{error}</div>}
          <div className={styles.field}>
            <label htmlFor="displayName">Your name</label>
            <input id="displayName" type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Jane Smith" required />
          </div>
          <div className={styles.field}>
            <label htmlFor="orgName">Organization name</label>
            <input id="orgName" type="text" value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="Acme Corp" required />
          </div>
          <div className={styles.field}>
            <label htmlFor="email">Work email</label>
            <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required autoComplete="email" />
          </div>
          <div className={styles.field}>
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={e => handlePasswordChange(e.target.value)}
              onFocus={() => setShowPasswordRequirements(true)}
              placeholder="At least 12 characters"
              required
              autoComplete="new-password"
            />
            {passwordStrength && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <div style={{ flex: 1, height: 4, background: '#e1dfdd', borderRadius: 2, overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        width: `${(passwordStrength.score / 6) * 100}%`,
                        background: getPasswordStrengthColor(passwordStrength.strength),
                        transition: 'all 0.3s ease',
                      }}
                    />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: getPasswordStrengthColor(passwordStrength.strength), textTransform: 'capitalize' }}>
                    {passwordStrength.strength}
                  </span>
                </div>
                {showPasswordRequirements && passwordStrength.feedback.length > 0 && (
                  <ul style={{ margin: '8px 0 0 0', padding: '0 0 0 20px', fontSize: 12, color: '#605e5c' }}>
                    {passwordStrength.feedback.map((msg, idx) => (
                      <li key={idx}>{msg}</li>
                    ))}
                  </ul>
                )}
                {showPasswordRequirements && passwordStrength.isValid && (
                  <div style={{ marginTop: 4, fontSize: 12, color: '#107c10', fontWeight: 600 }}>
                    ✓ Password meets all requirements
                  </div>
                )}
              </div>
            )}
            {showPasswordRequirements && !password && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#605e5c' }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Password must contain:</div>
                <ul style={{ margin: 0, padding: '0 0 0 20px' }}>
                  <li>At least 12 characters</li>
                  <li>One uppercase letter (A-Z)</li>
                  <li>One lowercase letter (a-z)</li>
                  <li>One number (0-9)</li>
                  <li>One special character (!@#$%...)</li>
                </ul>
              </div>
            )}
          </div>

          <div style={{ marginTop: 24, padding: '16px 20px', background: '#f9f9f9', borderRadius: 6, border: '1px solid #e1dfdd' }}>
            <div style={{ marginBottom: 12, fontSize: 13, fontWeight: 600, color: '#323130' }}>
              Legal agreements (required)
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12, cursor: 'pointer', fontSize: 13, color: '#605e5c' }}>
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={e => setAcceptedTerms(e.target.checked)}
                style={{ marginTop: 2, flexShrink: 0 }}
                required
              />
              <span>
                I agree to the <Link to="/terms" target="_blank" style={{ color: '#0078d4', textDecoration: 'none', fontWeight: 600 }}>Terms of Service</Link>
                {requiredDocs.find(d => d.document_type === 'terms') && (
                  <span style={{ color: '#a19f9d', marginLeft: 4 }}>(v{requiredDocs.find(d => d.document_type === 'terms')?.version})</span>
                )}
              </span>
            </label>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12, cursor: 'pointer', fontSize: 13, color: '#605e5c' }}>
              <input
                type="checkbox"
                checked={acceptedPrivacy}
                onChange={e => setAcceptedPrivacy(e.target.checked)}
                style={{ marginTop: 2, flexShrink: 0 }}
                required
              />
              <span>
                I agree to the <Link to="/privacy" target="_blank" style={{ color: '#0078d4', textDecoration: 'none', fontWeight: 600 }}>Privacy Policy</Link>
                {requiredDocs.find(d => d.document_type === 'privacy') && (
                  <span style={{ color: '#a19f9d', marginLeft: 4 }}>(v{requiredDocs.find(d => d.document_type === 'privacy')?.version})</span>
                )}
              </span>
            </label>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13, color: '#605e5c' }}>
              <input
                type="checkbox"
                checked={acceptedAUP}
                onChange={e => setAcceptedAUP(e.target.checked)}
                style={{ marginTop: 2, flexShrink: 0 }}
                required
              />
              <span>
                I agree to the <Link to="/aup" target="_blank" style={{ color: '#0078d4', textDecoration: 'none', fontWeight: 600 }}>Acceptable Use Policy</Link>
                {requiredDocs.find(d => d.document_type === 'aup') && (
                  <span style={{ color: '#a19f9d', marginLeft: 4 }}>(v{requiredDocs.find(d => d.document_type === 'aup')?.version})</span>
                )}
              </span>
            </label>
          </div>

          <button type="submit" className={styles.submitBtn} disabled={loading}>
            {loading ? 'Creating account...' : 'Create account'}
          </button>
        </form>

        <p className={styles.footer}>
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  )
}
